import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { CustomFieldInputs, type CustomField, type CustomKind, type CustomTarget, type CustomFieldsState } from '../../components/CustomFields.tsx';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface TargetRow {
  target: CustomTarget;
  label: string;
  active: number;
  total: number;
}

const TARGET_LABEL: Record<CustomTarget, MessageKey> = {
  'customer.customer': 'studio.t_customers',
  'product.product': 'studio.t_products',
  'purchase.supplier': 'studio.t_suppliers',
  'invoicing.invoice': 'studio.t_invoices',
  'crm.lead': 'studio.t_leads',
  'construction.project': 'studio.t_projects',
  'payroll.employee': 'studio.t_employees',
};
const TARGET_ICON: Record<CustomTarget, IconName> = {
  'customer.customer': 'users',
  'product.product': 'receipt',
  'purchase.supplier': 'bag',
  'invoicing.invoice': 'invoice',
  'crm.lead': 'trendUp',
  'construction.project': 'layers',
  'payroll.employee': 'cash',
};
const KINDS: CustomKind[] = ['text', 'long_text', 'number', 'money', 'date', 'yes_no', 'choice'];
const KIND_ICON: Record<CustomKind, IconName> = {
  text: 'edit',
  long_text: 'note',
  number: 'percent',
  money: 'cash',
  date: 'calendar',
  yes_no: 'checkCircle',
  choice: 'list',
};
const kindLabel = (k: CustomKind) => t(`studio.kind_${k}` as MessageKey);
const kindHint = (k: CustomKind) => t(`studio.kind_${k}_hint` as MessageKey);
const MAX = 40;

/**
 * Settings → Fields of your own: what a business wants to keep on its customers, products,
 * invoices and other records beyond what ERP PRO asks for. No code: name it, pick its kind,
 * and it shows on the record's form for everyone who works with those records.
 */
export function Fields() {
  const [params, setParams] = useSearchParams();
  const { data: targets, reload: reloadTargets } = useAction<TargetRow[]>('studio.targets');
  const target = targets?.find((x) => x.target === params.get('record'))?.target ?? targets?.[0]?.target;
  const [adding, setAdding] = useState(0);

  return (
    <ManageLayout
      title={t('manage.fields')}
      subtitle={t('studio.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setAdding((n) => n + 1)} disabled={!target || (targets?.find((x) => x.target === target)?.active ?? 0) >= MAX}>
          {t('studio.add')}
        </Button>
      }
    >
      {!targets ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !target ? (
        <EmptyState icon="sliders" title={t('studio.no_targets')} body={t('studio.no_targets_body')} />
      ) : (
        <>
          <div className="studio-targets" role="tablist" aria-label={t('studio.records')}>
            {targets.map((x) => (
              <button
                key={x.target}
                type="button"
                role="tab"
                aria-selected={x.target === target}
                className={`chip${x.target === target ? ' active' : ''}`}
                onClick={() => setParams({ record: x.target }, { replace: true })}
              >
                <Icon name={TARGET_ICON[x.target]} size={16} />
                <span>{t(TARGET_LABEL[x.target])}</span>
                {x.active ? <span className="studio-count tabular">{x.active}</span> : null}
              </button>
            ))}
          </div>
          <TargetFields key={target} target={target} adding={adding} onAdd={() => setAdding((n) => n + 1)} onChanged={() => void reloadTargets()} />
        </>
      )}
    </ManageLayout>
  );
}

function TargetFields({ target, adding, onAdd, onChanged }: { target: CustomTarget; adding: number; onAdd: () => void; onChanged: () => void }) {
  const { data, reload } = useAction<{ fields: CustomField[] }>('studio.fields', { target, all: true });
  const [editing, setEditing] = useState<CustomField | 'new' | null>(null);
  const [seen, setSeen] = useState(adding);
  const { busy, run } = useSave();
  // The page's "Add a field" button opens the dialog here.
  if (adding !== seen) {
    setSeen(adding);
    setEditing('new');
  }
  const fields = data?.fields ?? null;
  const open = fields?.filter((f) => f.active) ?? [];
  const away = fields?.filter((f) => !f.active) ?? [];
  const records = t(TARGET_LABEL[target]).toLowerCase();
  const move = (f: CustomField, up: boolean) =>
    void run(async () => {
      await action('studio.move_field', { id: f.id, up });
      await reload();
    });

  return (
    <>
      {!fields ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !open.length && !away.length ? (
        <EmptyState
          icon={TARGET_ICON[target]}
          title={t('studio.empty', { records })}
          body={t('studio.empty_body')}
          action={
            <Button variant="primary" icon="plus" onClick={onAdd}>
              {t('studio.add')}
            </Button>
          }
        />
      ) : (
        <>
          <p className="muted small">{t('studio.where', { records, n: open.length, max: MAX })}</p>
          {open.length ? (
            <div className="list-card" role="list">
              {open.map((f, i) => (
                <FieldRow key={f.id} f={f} onOpen={() => setEditing(f)}>
                  <span className="studio-move">
                    <IconButton icon="up" label={t('studio.move_up', { name: f.label })} disabled={busy || i === 0} onClick={() => move(f, true)} />
                    <IconButton icon="down" label={t('studio.move_down', { name: f.label })} disabled={busy || i === open.length - 1} onClick={() => move(f, false)} />
                  </span>
                </FieldRow>
              ))}
            </div>
          ) : null}
          {away.length ? (
            <>
              <h2 className="section-title">{t('studio.away_title')}</h2>
              <p className="muted small">{t('studio.away_hint')}</p>
              <div className="list-card" role="list">
                {away.map((f) => (
                  <FieldRow key={f.id} f={f} onOpen={() => setEditing(f)} />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
      {editing && fields ? (
        <FieldDialog
          target={target}
          field={editing === 'new' ? null : editing}
          others={fields.filter((f) => f.active && (editing === 'new' || f.id !== editing.id))}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function FieldRow({ f, onOpen, children }: { f: CustomField; onOpen: () => void; children?: React.ReactNode }) {
  return (
    <div role="listitem" className={`list-row studio-row${f.active ? '' : ' archived'}`}>
      <span className="list-icon" aria-hidden="true">
        <Icon name={KIND_ICON[f.kind]} size={18} />
      </span>
      <button className="list-main" onClick={onOpen}>
        <span className="list-title">{f.label}</span>
        <span className="list-sub">{[kindLabel(f.kind), f.kind === 'choice' && f.choices ? f.choices.join(', ') : null, f.help].filter(Boolean).join(' · ')}</span>
      </button>
      <span className="studio-end">
        {f.required ? <StatusBadge tone="warn" icon="alert" label={t('studio.required')} /> : null}
        {f.in_list ? <StatusBadge tone="info" icon="list" label={t('studio.in_list')} /> : null}
        {f.active ? null : <StatusBadge tone="neutral" icon="archive" label={t('studio.away')} />}
        {children}
      </span>
    </div>
  );
}

function FieldDialog({ target, field, others, onClose, onSaved }: { target: CustomTarget; field: CustomField | null; others: CustomField[]; onClose: () => void; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [label, setLabel] = useState(field?.label ?? '');
  const [kind, setKind] = useState<CustomKind>(field?.kind ?? 'text');
  const [choices, setChoices] = useState<string[]>(field?.choices?.length ? field.choices : ['', '']);
  const [help, setHelp] = useState(field?.help ?? '');
  const [required, setRequired] = useState(field?.required ?? false);
  const [inList, setInList] = useState(field?.in_list ?? false);
  const [tried, setTried] = useState(false);
  const [puttingAway, setPuttingAway] = useState(false);
  const [sample, setSample] = useState<Record<string, string | boolean>>({});

  const clean = choices.map((c) => c.trim()).filter(Boolean);
  const errors = {
    label: !label.trim() ? t('form.required') : others.some((o) => o.label.toLowerCase() === label.trim().toLowerCase()) ? t('studio.name_taken') : null,
    choices: kind !== 'choice' ? null : new Set(clean).size < 2 ? t('studio.choices_rule') : new Set(clean.map((c) => c.toLowerCase())).size !== clean.length ? t('studio.choices_twice') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      await action('studio.save_field', {
        ...(field ? { id: field.id } : {}),
        target,
        label: label.trim(),
        kind,
        choices: kind === 'choice' ? clean : null,
        required,
        in_list: inList,
        help: help.trim(),
      });
      onSaved();
    }, field ? t('studio.saved', { name: label.trim() }) : t('studio.added', { name: label.trim() }));
  };
  const setActive = (active: boolean) =>
    void run(async () => {
      await action('studio.set_field_active', { id: field!.id, active });
      onSaved();
    }, active ? t('studio.back_done', { name: field!.label }) : t('studio.away_done', { name: field!.label })).finally(() => setPuttingAway(false));

  // A form the way people will see it, to try the field before saving it.
  const previewField: CustomField = {
    id: 'preview',
    target,
    key: 'preview',
    label: label.trim() || t('studio.name'),
    kind,
    choices: clean,
    required,
    in_list: inList,
    help: help.trim() || null,
    sequence: 0,
    active: true,
  };
  const preview: CustomFieldsState = {
    fields: [previewField],
    draft: sample,
    set: (k, v) => setSample((s) => ({ ...s, [k]: v })),
    errors: {},
    valid: true,
    editable: true,
    save: async () => true,
  };

  return (
    <Dialog
      title={field ? field.label : t('studio.add')}
      onClose={onClose}
      footer={
        <>
          {field ? (
            field.active ? (
              <Button variant="ghost" icon="archive" className="dialog-left" onClick={() => setPuttingAway(true)} disabled={busy}>
                {t('studio.put_away')}
              </Button>
            ) : (
              <Button variant="ghost" icon="undo" className="dialog-left" onClick={() => setActive(true)} disabled={busy}>
                {t('studio.bring_back')}
              </Button>
            )
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {field ? t('common.save') : t('studio.add_short')}
          </Button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Field label={t('studio.name')} hint={t('studio.name_hint')} error={tried ? errors.label : null} required>
          <input className="input" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('studio.kind')} hint={field ? t('studio.kind_fixed') : kindHint(kind)}>
          <select className="input" value={kind} disabled={!!field} onChange={(e) => setKind(e.target.value as CustomKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {kindLabel(k)}
              </option>
            ))}
          </select>
        </Field>
      </form>
      {kind === 'choice' ? (
        <Field label={t('studio.choices')} hint={t('studio.choices_hint')} error={tried ? errors.choices : null}>
          <div className="studio-choices">
            {choices.map((c, i) => (
              <div key={i} className="studio-choice">
                <input
                  className="input"
                  value={c}
                  maxLength={60}
                  aria-label={t('studio.choice_n', { n: i + 1 })}
                  onChange={(e) => setChoices((x) => x.map((y, j) => (j === i ? e.target.value : y)))}
                />
                <IconButton icon="x" label={t('studio.choice_remove', { n: i + 1 })} disabled={choices.length <= 2} onClick={() => setChoices((x) => x.filter((_, j) => j !== i))} />
              </div>
            ))}
            <Button variant="ghost" icon="plus" size="sm" onClick={() => setChoices((x) => [...x, ''])} disabled={choices.length >= 50}>
              {t('studio.choice_add')}
            </Button>
          </div>
        </Field>
      ) : null}
      <Field label={t('studio.help')} hint={t('studio.help_hint')}>
        <input className="input" value={help} maxLength={200} onChange={(e) => setHelp(e.target.value)} />
      </Field>
      {kind !== 'yes_no' ? (
        <div className="check-row">
          <Switch checked={required} onChange={setRequired} label={t('studio.required')} />
          <span>
            <span className="strong">{t('studio.required')}</span>
            <span className="muted">{t('studio.required_hint')}</span>
          </span>
        </div>
      ) : null}
      <div className="check-row">
        <Switch checked={inList} onChange={setInList} label={t('studio.in_list')} />
        <span>
          <span className="strong">{t('studio.in_list')}</span>
          <span className="muted">{t('studio.in_list_hint')}</span>
        </span>
      </div>
      <div className="studio-preview" aria-label={t('studio.preview')}>
        <span className="muted small">{t('studio.preview_hint')}</span>
        <CustomFieldInputs cf={preview} />
      </div>
      {puttingAway && field ? (
        <Confirm
          title={t('studio.away_confirm', { name: field.label })}
          body={t('studio.away_body')}
          confirm={t('studio.put_away')}
          busy={busy}
          onConfirm={() => setActive(false)}
          onClose={() => setPuttingAway(false)}
        />
      ) : null}
    </Dialog>
  );
}
